import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import Layout from '../../components/Layout';
import { patientAPI } from '../../services/api';

const DoctorProfileView = () => {
  const { uid } = useParams();
  const navigate = useNavigate();
  const [doctor, setDoctor] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadDoctor = async () => {
      try {
        const res = await patientAPI.getDoctorDetail(uid);
        setDoctor(res.data || null);
      } catch (error) {
        toast.error(error.message || 'Unable to load doctor profile.');
      } finally {
        setLoading(false);
      }
    };

    loadDoctor();
  }, [uid]);

  if (loading) {
    return (
      <Layout title="Doctor Profile" role="patient" showBack>
        <div className="p-4 space-y-4">
          {[1, 2, 3].map((item) => <div key={item} className="skeleton h-24 rounded-2xl" />)}
        </div>
      </Layout>
    );
  }

  if (!doctor) {
    return (
      <Layout title="Doctor Profile" role="patient" showBack>
        <div className="p-4">
          <div className="card text-center py-10 text-gray-500">
            Doctor profile is unavailable.
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Doctor Profile" role="patient" showBack>
      <div className="p-4 pb-24 space-y-4">
        <div className="card flex items-center gap-4">
          <div className="w-16 h-16 bg-accent-100 rounded-full flex items-center justify-center">
            <span className="text-accent-700 text-2xl font-bold">
              {(doctor.full_name || 'D').charAt(0)}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-lg font-bold text-gray-900 truncate">Dr. {doctor.full_name}</h2>
            <p className="text-sm text-accent-600">{doctor.specialization || 'General Practice'}</p>
            {doctor.hospital_affiliation && (
              <p className="text-xs text-gray-500 mt-1">🏥 {doctor.hospital_affiliation}</p>
            )}
          </div>
        </div>

        <div className="card space-y-4">
          <h3 className="font-semibold text-gray-900">Professional Details</h3>
          {[
            ['License Number', doctor.license_number],
            ['Email', doctor.email],
            ['Phone Number', doctor.phone_number],
            ['County', doctor.county],
            ['Experience', doctor.years_of_experience ? `${doctor.years_of_experience} years` : '—'],
            ['Consultation Fee', `KES ${doctor.consultation_fee || 500}`],
          ].map(([label, value]) => (
            <div key={label}>
              <label className="label">{label}</label>
              <p className="text-gray-900 break-words">{value || '—'}</p>
            </div>
          ))}
        </div>

        <div className="card space-y-3">
          <h3 className="font-semibold text-gray-900">Services</h3>
          {(doctor.services_offered || []).length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {doctor.services_offered.map((service) => (
                <span key={service} className="px-3 py-1 bg-primary-50 text-primary-700 text-sm rounded-full">
                  {service}
                </span>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500">No services listed.</p>
          )}
          <div>
            <label className="label">Biography</label>
            <p className="text-sm text-gray-700">{doctor.bio || 'No biography available.'}</p>
          </div>
        </div>

        <div className="card space-y-3">
          <h3 className="font-semibold text-gray-900">Availability</h3>
          {(doctor.available_days || []).length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {doctor.available_days.map((day) => (
                <span key={day} className="px-3 py-1 bg-green-50 text-green-700 text-sm rounded-full">
                  {day}
                </span>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500">Availability has not been shared yet.</p>
          )}
        </div>

        <button
          onClick={() => navigate(`/patient/appointments/book?doctor=${doctor.uid}`)}
          className="btn-primary w-full"
        >
          Book Appointment
        </button>
      </div>
    </Layout>
  );
};

export default DoctorProfileView;
